// Loading the custom overworld art (docs/70) in the browser: the index of every file and its
// status, and one folder's tiles (a biome, a season, a time of day) as images by tile and variant.
//
// A build loads one atlas per folder and time of day (`virtual:fk-overworld-atlas`, docs/143 OW6): an
// image of every file in a grid and the list of their names. The dev server has no atlases; it serves
// the files one by one from `apps/client/art/overworld/` (`scripts/art-studio`), so the Studio's saves
// show at once.
import atlas from 'virtual:fk-overworld-atlas';
import { ART_URL, fileName, LIT_COLOURS, parseFileName, type ArtSeason, type ArtStatus, type Biome, type TimeOfDay } from './customTiles';
import { PALETTES } from './overworldPalettes';

/** Whether a folder's night art lights its own windows (docs/132 OP17): its palette holds the lit colours.
 *  Where it does, the chart draws the lit pixels instead of the lamp glows at places. */
export const folderHasLights = (biome: Biome, season: ArtSeason): boolean => LIT_COLOURS.every((c) => PALETTES[biome][season].includes(c));

export interface ArtIndex {
  /** Files by folder (`temperate/spring` → `forest_day_01.png`, …), in the manifest's order. */
  files: Record<string, string[]>;
  /** Status by path (`temperate/spring/forest_day_01.png`); a file missing here is a draft. */
  status: Record<string, ArtStatus>;
}

export async function fetchArtIndex(): Promise<ArtIndex | undefined> {
  try {
    const r = await fetch('/__art-studio/overworld/index', { cache: 'no-store' });
    return r.ok ? ((await r.json()) as ArtIndex) : undefined;
  } catch { return undefined; }
}

/** The variants of each tile in a folder at one time of day: tile → variant numbers, ascending (frames aside). */
export function folderVariants(index: ArtIndex, biome: Biome, season: ArtSeason, time: TimeOfDay): Map<string, number[]> {
  const out = new Map<string, number[]>();
  for (const name of index.files[`${biome}/${season}`] ?? []) {
    const f = parseFileName(name);
    if (!f || f.time !== time || f.frame) continue;
    out.set(f.tile, [...(out.get(f.tile) ?? []), f.variant].sort((a, b) => a - b));
  }
  return out;
}

/** How many frames each animated variant has in a folder at one time of day: `tile:variant` → frames (CO10). */
export function folderFrames(index: ArtIndex, biome: Biome, season: ArtSeason, time: TimeOfDay): Map<string, number> {
  const out = new Map<string, number>();
  for (const name of index.files[`${biome}/${season}`] ?? []) {
    const f = parseFileName(name);
    if (f?.frame && f.time === time) out.set(`${f.tile}:${f.variant}`, Math.max(f.frame, out.get(`${f.tile}:${f.variant}`) ?? 1));
  }
  return out;
}

export async function loadImage(src: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.src = src;
  await img.decode();
  return img;
}

/** What a custom chart is drawn from: each tile's variants, how many frames one has, and the image of a frame. */
export interface CustomArt {
  variants: (tile: string) => readonly number[];
  image: (tile: string, variant: number, frame?: number) => CanvasImageSource | undefined;
  /** Frames of a variant (1 when it doesn't move). */
  frames?: (tile: string, variant: number) => number;
}

/** One cell of an atlas, drawn by `drawImage(source, sx, sy, 24, 24, …)`: a canvas cut from the atlas. */
function cutCell(img: HTMLImageElement, i: number, cols: number, cell: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = cell;
  // kept in memory, not on the GPU: the chart is painted on in-memory canvases, and a GPU-backed source is read back
  // on every draw (docs/185 PB13: eight regions' folders, ~1,600 cells, took the paint from under a second to 30 s)
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, (i % cols) * cell, Math.floor(i / cols) * cell, cell, cell, 0, 0, cell, cell);
  return c;
}

/** A folder's files at one time of day from the build's atlas: name → its cell, cut out. */
async function loadAtlas(biome: Biome, season: ArtSeason, time: TimeOfDay): Promise<Map<string, CanvasImageSource> | undefined> {
  const packs = atlas, folder = packs?.folders[`${biome}/${season}/${time}`];
  if (!packs || !folder) return undefined;
  const [img, files] = await Promise.all([loadImage(folder.png), fetch(folder.files).then((r) => r.json() as Promise<string[]>)]);
  return new Map(files.map((name, i) => [name, cutCell(img, i, packs.cols, packs.cell)]));
}

const NONE: readonly number[] = [];
const folders = new Map<string, Promise<CustomArt | undefined>>();
/** One folder's art at one time of day, loaded once per page (the World screen; the Studio keeps its own). */
export function loadCustomArt(biome: Biome, season: ArtSeason, time: TimeOfDay): Promise<CustomArt | undefined> {
  const key = `${biome}/${season}/${time}`;
  let p = folders.get(key);
  if (!p) {
    p = (async () => {
      const packed = await loadAtlas(biome, season, time).catch(() => undefined);
      const index: ArtIndex | undefined = packed ? { files: { [`${biome}/${season}`]: [...packed.keys()] }, status: {} } : await fetchArtIndex();
      if (!index) return undefined;
      const variants = folderVariants(index, biome, season, time), frames = folderFrames(index, biome, season, time);
      const images = new Map<string, CanvasImageSource>();
      // every frame of every variant (a still tile has one)
      const wanted = [...variants].flatMap(([tile, vs]) => vs.flatMap((v) => Array.from({ length: frames.get(`${tile}:${v}`) ?? 1 }, (_, k) => ({ tile, v, f: k + 1 }))));
      await Promise.all(wanted.map(async ({ tile, v, f }) => {
        const name = fileName({ tile, time, variant: v, frame: f });
        const cut = packed?.get(name);
        if (cut) { images.set(`${tile}:${v}:${f}`, cut); return; }
        if (packed) return;
        try { images.set(`${tile}:${v}:${f}`, await loadImage(`${ART_URL}/${biome}/${season}/${name}`)); } catch { /* drawn as missing */ }
      }));
      return {
        variants: (t) => variants.get(t) ?? NONE,
        image: (t, v, f = 1) => images.get(`${t}:${v}:${f}`) ?? images.get(`${t}:${v}:1`),
        frames: (t, v) => frames.get(`${t}:${v}`) ?? 1,
      };
    })();
    folders.set(key, p);
  }
  return p;
}
